@if (isLoading()) {
  <div class="flex items-center justify-center p-12">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-9"></div>
  </div>
} @else if (agentProfile(); as profile) {
  @switch (showMode()) {
    @case ('completion') {
      @if (isCompletionVisible()) {
        <!-- Jauge de complétude du profil (item fond blanc) -->
        <div class="bg-white rounded-2xl border border-neutral-3 p-5 shadow-xs space-y-3 relative">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div class="flex items-center gap-3.5 min-w-0 pr-8 sm:pr-0">
              <div
                class="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm shrink-0"
                [class.bg-success-2]="profileCompletion() === 100"
                [class.text-success-11]="profileCompletion() === 100"
                [class.bg-primary-2]="profileCompletion() < 100"
                [class.text-primary-11]="profileCompletion() < 100"
              >
                @if (profileCompletion() === 100) {
                  <mat-icon class="text-success-11">check</mat-icon>
                } @else {
                  {{ profileCompletion() }}%
                }
              </div>
              <div class="min-w-0">
                <h3 class="text-base font-bold text-neutral-12">
                  @if (profileCompletion() === 100) {
                    Profil 100% complété !
                  } @else {
                    Complétude de votre profil
                  }
                </h3>
                <p class="text-xs text-neutral-11 whitespace-normal break-words">
                  @if (profileCompletion() === 100) {
                    Félicitations ! Votre profil est complet et optimisé pour recevoir des mandats.
                  } @else {
                    Un profil détaillé inspire davantage confiance aux propriétaires vendeurs.
                  }
                </p>
              </div>
            </div>

            @if (profileCompletion() < 100) {
              <span
                class="text-xs font-semibold px-3 py-1 bg-neutral-2 text-neutral-11 border border-neutral-3 rounded-full text-center whitespace-normal self-start sm:self-auto"
              >
                {{ completionTip() }}
              </span>
            } @else {
              <!-- Croix pour fermer / masquer l'information de complétude -->
              <button
                type="button"
                mat-icon-button
                class="!absolute top-3 right-3 text-neutral-9 hover:text-neutral-12"
                (click)="onDismissCompletion()"
                aria-label="Fermer"
              >
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>

          <!-- Barre de progression -->
          <div class="w-full bg-neutral-2 h-2 rounded-full overflow-hidden border border-neutral-3">
            <div
              class="h-full rounded-full transition-all duration-500 ease-out"
              [class.bg-success-9]="profileCompletion() === 100"
              [class.bg-primary-9]="profileCompletion() < 100"
              [style.width.%]="profileCompletion()"
            ></div>
          </div>
        </div>
        <mat-divider class="my-4"></mat-divider>
      }
    }

    @case ('responsiveness') {
      <!-- Score de réactivité de l'agent -->
      <div
        class="bg-white dark:bg-neutral-2 rounded-2xl border border-neutral-3 p-5 sm:p-6 shadow-xs space-y-4"
      >
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div class="flex items-center gap-3.5">
            <div
              class="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              [class.bg-emerald-100]="isHighlyResponsive()"
              [class.text-emerald-700]="isHighlyResponsive()"
              [class.dark:bg-emerald-950/60]="isHighlyResponsive()"
              [class.dark:text-emerald-300]="isHighlyResponsive()"
              [class.bg-primary-2]="!isHighlyResponsive()"
              [class.text-primary-11]="!isHighlyResponsive()"
            >
              <mat-icon class="size-6">{{ isHighlyResponsive() ? 'bolt' : 'speed' }}</mat-icon>
            </div>
            <div>
              <div class="flex items-center gap-2 flex-wrap">
                <h3 class="text-base font-bold text-neutral-12 m-0">Score de réactivité</h3>
                @if (isHighlyResponsive()) {
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 shadow-2xs"
                  >
                    <mat-icon class="size-3.5! text-xs! text-emerald-600 dark:text-emerald-400"
                      >bolt</mat-icon
                    >
                    <span>Agent très réactif</span>
                  </span>
                }
              </div>
              <p class="text-xs text-neutral-11 m-0 mt-0.5">
                Mesure de votre assiduité lors des demandes de visites et des propositions de mandat.
              </p>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div
            class="bg-neutral-1 dark:bg-neutral-3/30 p-3.5 rounded-xl border border-neutral-3 flex flex-col justify-between"
          >
            <span class="text-xs font-medium text-neutral-10">Temps de réponse moyen</span>
            <span class="text-xl font-extrabold text-neutral-12 mt-1">
              {{ formattedResponseTime() || '< 1 h' }}
            </span>
            <span class="text-[11px] text-neutral-10 mt-0.5">Objectif : sous 4 h</span>
          </div>

          <div
            class="bg-neutral-1 dark:bg-neutral-3/30 p-3.5 rounded-xl border border-neutral-3 flex flex-col justify-between"
          >
            <span class="text-xs font-medium text-neutral-10">Taux de réponse</span>
            <span class="text-xl font-extrabold text-neutral-12 mt-1">
              {{ responseRatePercent() !== null ? responseRatePercent() + ' %' : '100 %' }}
            </span>
            <span class="text-[11px] text-neutral-10 mt-0.5">Délai limite : 24 h</span>
          </div>

          <div
            class="bg-neutral-1 dark:bg-neutral-3/30 p-3.5 rounded-xl border border-neutral-3 flex flex-col justify-between"
          >
            <span class="text-xs font-medium text-neutral-10">Expirations de délai</span>
            <span
              class="text-xl font-extrabold mt-1"
              [class.text-emerald-600]="expiredActionsCount() === 0"
              [class.text-red-9]="expiredActionsCount() > 0"
            >
              {{ expiredActionsCount() }} {{ expiredActionsCount() > 1 ? 'expirations' : 'expiration' }}
            </span>
            <span class="text-[11px] text-neutral-10 mt-0.5">Pénalise votre score</span>
          </div>
        </div>

        <div
          class="p-3 bg-neutral-1 dark:bg-neutral-3/20 rounded-xl border border-neutral-3/60 text-xs text-neutral-10 leading-relaxed flex items-start gap-2"
        >
          <mat-icon class="size-4! text-sm! text-primary-9 shrink-0 mt-0.5">info</mat-icon>
          <span>
            Les vendeurs privilégient les agents réactifs. Chaque expiration de délai de 24 h entraîne une
            pénalité sur votre temps de réponse moyen et révoque le badge « Agent très réactif ».
          </span>
        </div>
      </div>
      <mat-divider class="my-4"></mat-divider>
    }

    @case ('network') {
      <!-- Type de profil & Réseau (item fond blanc style card) -->
      <div class="bg-white rounded-2xl border border-neutral-3 p-5 shadow-xs flex items-start gap-4">
        <div class="p-3 rounded-xl bg-primary-2 text-primary-11 shrink-0">
          <mat-icon class="size-6">badge</mat-icon>
        </div>
        <div class="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div class="flex flex-col">
            <span class="text-xs text-neutral-10 font-medium uppercase tracking-wider"
              >Type d'exercice & Réseau</span
            >
            <div class="flex items-center gap-3 mt-1 flex-wrap">
              <span class="text-lg font-bold text-neutral-12">
                @if (profile.type === 'titulaire') {
                  Titulaire Carte T
                } @else {
                  Mandataire Immobilier
                }
              </span>
              @if (network()) {
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold bg-primary-2 text-primary-11 border border-primary-4"
                >
                  {{ network() }}
                </span>
              }
            </div>
            <span class="text-xs text-neutral-10 mt-1">
              @if (profile.type === 'titulaire') {
                Détenteur légal de la carte professionnelle d'agent immobilier.
              } @else {
                Agent commercial ou mandataire rattaché à une agence ou réseau titulaire.
              }
            </span>
          </div>

          @if (showLinkToAgentPage()) {
            <div class="shrink-0">
              <a
                [routerLink]="['/agents', profile._id]"
                class="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold text-primary-11 bg-primary-2 hover:bg-primary-3 border border-primary-4 rounded-lg transition-all cursor-pointer font-sans"
              >
                <mat-icon class="size-4 !text-sm">badge</mat-icon>
                <span>Fiche agent détaillée</span>
              </a>
            </div>
          }
        </div>
      </div>
    }

    @case ('specialties') {
      <!-- Spécialités (items fond blanc style card) -->
      @if (specialties() && specialties().length > 0) {
        <div class="flex flex-wrap gap-3">
          @for (spec of specialties(); track spec) {
            @let info = getSpecialtyInfo(spec);
            <div
              class="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-xs font-semibold bg-white text-neutral-12 border border-neutral-3 shadow-xs hover:border-neutral-4 transition-colors"
            >
              <div
                class="w-7 h-7 rounded-lg bg-primary-2 text-primary-11 flex items-center justify-center shrink-0"
              >
                <mat-icon class="!w-4 !h-4 !text-base">{{ info.icon }}</mat-icon>
              </div>
              <span class="font-bold">{{ info.label }}</span>
            </div>
          }
        </div>
      } @else {
        <div class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-2xl bg-white shadow-xs">
          <p class="text-xs text-neutral-9 italic">
            Aucune spécialité de transactions sélectionnée pour le moment.
          </p>
        </div>
      }
    }

    @case ('services') {
      <!-- Services & Prestations offertes -->
      @if (services() && services().length > 0) {
        <div class="flex flex-wrap gap-3">
          @for (srv of services(); track srv) {
            @let srvInfo = getServiceInfo(srv);
            <div
              class="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-xs font-semibold bg-white text-neutral-12 border border-neutral-3 shadow-xs hover:border-neutral-4 transition-colors"
            >
              <div
                class="w-7 h-7 rounded-lg bg-primary-2 text-primary-11 flex items-center justify-center shrink-0"
              >
                <mat-icon class="!w-4 !h-4 !text-base">{{ srvInfo.icon }}</mat-icon>
              </div>
              <span class="font-bold">{{ srvInfo.label }}</span>
            </div>
          }
        </div>
      } @else {
        <div class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-2xl bg-white shadow-xs">
          <p class="text-xs text-neutral-9 italic">
            Aucune prestation de service spécifique renseignée pour le moment.
          </p>
        </div>
      }
    }

    @case ('activeCard') {
      <!-- Active Card Presentation -->
      <div class="flex flex-col gap-4">
        <div class="flex items-center text-xl leading-none font-semibold tracking-tight">
          Carte professionnelle active
        </div>

        @if (activeCard(); as card) {
          <div
            class="bg-white dark:bg-neutral-2 rounded-2xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-xs flex flex-col items-center gap-8"
          >
            <!-- The 3D flip card -->
            <div class="w-full max-w-sm shrink-0">
              <etxe-agent-card-display [card]="card"></etxe-agent-card-display>
            </div>

            <!-- Quick textual list of card values for copy/quick inspection -->
            <div
              class="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 text-sm text-neutral-11"
            >
              <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-4">
                <span class="text-xs text-neutral-9 font-medium">Numéro de carte</span>
                <span class="font-semibold text-neutral-12 mt-0.5">{{ card.number }}</span>
              </div>
              <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-4">
                <span class="text-xs text-neutral-9 font-medium">Nom de l'entreprise</span>
                <span class="font-semibold text-neutral-12 mt-0.5">{{ card.companyName }}</span>
              </div>
              <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-4">
                <span class="text-xs text-neutral-9 font-medium">Représentant légal / dirigeant</span>
                <span class="font-semibold text-neutral-12 mt-0.5">
                  {{ card.leader.firstName }}
                  {{ card.leader.lastName | uppercase }}
                </span>
              </div>
              <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-4">
                <span class="text-xs text-neutral-9 font-medium">SIREN</span>
                <span class="font-semibold text-neutral-12 mt-0.5 font-mono">{{ card.siren }}</span>
              </div>
              <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-4">
                <span class="text-xs text-neutral-9 font-medium">Date d'expiration</span>
                <span
                  class="font-semibold text-neutral-12 mt-0.5"
                  [class.text-error-11]="card.isActive === false"
                >
                  {{ card.expiresAt | date: 'dd/MM/yyyy' }}
                  @if (card.isActive === false) {
                    (Expirée)
                  }
                </span>
              </div>
              <div class="flex flex-col pb-2 border-b border-neutral-2 dark:border-neutral-4">
                <span class="text-xs text-neutral-9 font-medium">CCI émettrice</span>
                <span class="font-semibold text-neutral-12 mt-0.5">{{ card.cci.name }}</span>
              </div>
            </div>
          </div>
        } @else {
          <div
            class="p-6 md:p-8 rounded-2xl border-2 border-dashed border-amber-6 dark:border-amber-7 bg-amber-1 dark:bg-amber-2/30 flex flex-col items-center text-center gap-4 shadow-xs"
          >
            <div
              class="w-14 h-14 rounded-2xl bg-amber-3 dark:bg-amber-3/80 text-amber-11 flex items-center justify-center shadow-xs"
            >
              <mat-icon class="!w-7 !h-7 !text-3xl">credit_card_off</mat-icon>
            </div>

            <div class="flex flex-col gap-1.5 max-w-lg">
              <h4 class="text-base font-bold text-neutral-12">Aucune carte professionnelle active trouvée</h4>
              <p class="text-sm text-neutral-11 leading-relaxed">
                @if (expiredCards() && expiredCards().length > 0) {
                  Votre précédente carte professionnelle T est arrivée à échéance et a expiré. Elle a été
                  automatiquement archivée dans votre historique ci-dessous.
                } @else {
                  Aucune carte professionnelle active n'a été détectée sur votre compte. Votre carte a
                  peut-être expiré lors de la vérification quotidienne ou doit être réenregistrée.
                }
              </p>
            </div>

            <div
              class="flex items-center gap-2 p-3 rounded-xl bg-amber-2 border border-amber-5 text-amber-12 text-xs font-medium max-w-lg text-left"
            >
              <mat-icon class="text-amber-11 size-5 shrink-0">info</mat-icon>
              <span>
                <strong>Obligation légale (Loi Hoguet) :</strong> La détention d'une Carte T valide est
                obligatoire pour exercer en tant qu'agent immobilier et recevoir des mandats.
              </span>
            </div>

            @if (expiredCards() && expiredCards().length > 0) {
              <div
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-3 text-amber-11 text-xs font-semibold mt-1"
              >
                <mat-icon class="!w-4 !h-4 !text-sm">history</mat-icon>
                <span>{{ expiredCards().length }} carte(s) T archivée(s) dans l'historique</span>
              </div>
            }
          </div>
        }
      </div>
      <mat-divider class="my-4"></mat-divider>
    }

    @case ('cities') {
      <!-- Villes prioritaires pour les alertes email (items fond blanc style card) -->
      @if (preferredCities() && preferredCities().length > 0) {
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <span class="text-xs text-neutral-10 font-medium">
              {{ preferredCities().length }} / 5 communes suivies
            </span>
            <span class="text-xs font-semibold text-primary-11 flex items-center gap-1">
              <mat-icon class="!w-3.5 !h-3.5 !text-xs">bolt</mat-icon>
              Alertes email en temps réel
            </span>
          </div>

          <div class="flex flex-wrap gap-2.5">
            @for (city of preferredCities(); track city.name + '-' + city.zipCode) {
              <div
                class="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-xs font-bold bg-white text-neutral-12 border border-neutral-3 shadow-xs hover:border-neutral-4 transition-colors"
              >
                <div
                  class="w-6 h-6 rounded-lg bg-primary-2 text-primary-11 flex items-center justify-center shrink-0"
                >
                  <mat-icon class="!w-3.5 !h-3.5 !text-sm">mark_email_unread</mat-icon>
                </div>
                <span>{{ city.name }}</span>
                <span class="font-mono text-neutral-10 text-[11px]">({{ city.zipCode }})</span>
              </div>
            }
          </div>
        </div>
      } @else {
        <div class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-2xl bg-white shadow-xs">
          <p class="text-xs text-neutral-9 italic">Aucune commune prioritaire configurée pour les alertes.</p>
        </div>
      }
    }

    @case ('zones') {
      <!-- Secteur d'intervention (items fond blanc style card) -->
      <div class="space-y-4">
        <!-- Région administrative principale (fond blanc card) -->
        @if (primaryRegion()) {
          <div class="flex items-center gap-3.5 p-4 rounded-2xl bg-white border border-neutral-3 shadow-xs">
            <div
              class="w-10 h-10 rounded-xl bg-primary-2 text-primary-11 flex items-center justify-center shrink-0"
            >
              <mat-icon class="!w-5 !h-5 !text-xl">explore</mat-icon>
            </div>
            <div class="flex flex-col min-w-0">
              <span class="text-[11px] font-bold text-neutral-10 uppercase tracking-wider">
                Région administrative principale
              </span>
              <span class="text-sm font-extrabold text-neutral-12 truncate">
                {{ getRegionName(primaryRegion()) }}
              </span>
            </div>
          </div>
        }

        <!-- Zones et départements d'intervention -->
        @if (interventionZones() && interventionZones().length > 0) {
          @for (zone of interventionZones(); track zone.country) {
            @let regionGroups = getDepartmentsByRegion(zone.departments);
            <div class="flex flex-col gap-3">
              <!-- Territoire national : France (fond blanc card) -->
              <div
                class="flex items-center justify-between p-3.5 bg-white rounded-xl border border-neutral-3 shadow-xs"
              >
                <span
                  class="text-xs font-bold text-neutral-12 uppercase tracking-wider flex items-center gap-2"
                >
                  <span
                    class="w-7 h-7 rounded-lg bg-neutral-2 text-neutral-11 flex items-center justify-center shrink-0"
                  >
                    <mat-icon class="!w-4 !h-4 !text-base">public</mat-icon>
                  </span>
                  <span>Territoire national : {{ zone.country === 'FR' ? 'France' : zone.country }}</span>
                </span>
                <span
                  class="text-xs font-semibold px-2.5 py-1 rounded-full bg-neutral-2 text-neutral-11 border border-neutral-3"
                >
                  {{ zone.departments?.length || 0 }} département(s) couvert(s)
                </span>
              </div>

              @if (regionGroups.length > 0) {
                <div class="flex flex-col gap-3">
                  @for (group of regionGroups; track group.regionName) {
                    <div
                      class="flex flex-col gap-2.5 p-4 rounded-2xl border transition-all shadow-xs"
                      [class.bg-white]="!group.isPrimary"
                      [class.border-neutral-3]="!group.isPrimary"
                      [class.bg-primary-1/30]="group.isPrimary"
                      [class.border-primary-4]="group.isPrimary"
                    >
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2">
                          <mat-icon
                            class="!w-4 !h-4 !text-base"
                            [class.text-primary-11]="group.isPrimary"
                            [class.text-neutral-9]="!group.isPrimary"
                          >
                            place
                          </mat-icon>
                          <span class="text-xs font-bold text-neutral-12">
                            {{ group.regionName }}
                          </span>
                          @if (group.isPrimary) {
                            <span
                              class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary-3 text-primary-11"
                            >
                              Région principale
                            </span>
                          }
                        </div>
                        <span
                          class="text-[11px] font-mono font-medium text-neutral-10 bg-neutral-3 px-2 py-0.5 rounded-full"
                        >
                          {{ group.departments.length }} dép.
                        </span>
                      </div>

                      <div class="flex flex-wrap gap-2 pt-1">
                        @for (dept of group.departments; track dept.code) {
                          <span
                            class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs bg-white text-neutral-12 border border-neutral-3 shadow-2xs hover:border-neutral-4 transition-colors"
                          >
                            <span
                              class="font-mono text-[11px] font-extrabold px-1.5 py-0.5 rounded-md"
                              [class.bg-primary-2]="group.isPrimary"
                              [class.text-primary-11]="group.isPrimary"
                              [class.bg-neutral-3]="!group.isPrimary"
                              [class.text-neutral-11]="!group.isPrimary"
                            >
                              {{ dept.code }}
                            </span>
                            <span class="font-medium">{{ dept.name }}</span>
                          </span>
                        }
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <div
                  class="p-4 bg-white rounded-xl border border-primary-4 shadow-xs flex items-center gap-3"
                >
                  <mat-icon class="text-primary-11">public</mat-icon>
                  <div class="flex flex-col">
                    <span class="text-xs font-bold text-primary-12">Couverture nationale intégrale</span>
                    <span class="text-xs text-neutral-11"
                      >Actif sur l'ensemble des départements français.</span
                    >
                  </div>
                </div>
              }
            </div>
          }
        } @else {
          <div class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-2xl bg-white shadow-xs">
            <mat-icon class="!w-8 !h-8 !text-2xl text-neutral-8 mb-2">map</mat-icon>
            <p class="text-sm font-semibold text-neutral-11">Aucun secteur d'intervention spécifié</p>
            <p class="text-xs text-neutral-9 mt-0.5">
              Configurez votre région et vos départements d'exercice.
            </p>
          </div>
        }
      </div>
    }

    @case ('bio') {
      <!-- Présentation & Biographie (fond blanc style card) -->
      @if (bio() && bio().trim().length > 0) {
        <div
          class="p-5 bg-white rounded-2xl border border-neutral-3 shadow-xs text-sm text-neutral-12 leading-relaxed whitespace-pre-line"
        >
          {{ bio() }}
        </div>
      } @else {
        <div
          class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-2xl bg-white shadow-xs max-w-full"
        >
          <p class="text-xs text-neutral-9 italic whitespace-normal break-words max-w-md mx-auto">
            Aucune présentation rédigée pour le moment. Présentez votre parcours et vos atouts pour inspirer
            confiance aux vendeurs.
          </p>
        </div>
      }
    }

    @case ('socialLinks') {
      <!-- Réseaux sociaux & Liens web (items fond blanc style card) -->
      @if (hasSocialLinks()) {
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          @if (socialLinks()?.website) {
            <a
              [href]="socialLinks()!.website"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 p-4 bg-white hover:bg-neutral-1 border border-neutral-3 hover:border-neutral-4 rounded-2xl transition-all group shadow-xs cursor-pointer"
            >
              <div
                class="w-9 h-9 rounded-lg bg-primary-2 text-primary-11 flex items-center justify-center shrink-0"
              >
                <mat-icon class="!w-4.5 !h-4.5 !text-lg">language</mat-icon>
              </div>
              <div class="flex flex-col min-w-0 flex-1">
                <span class="text-[11px] font-semibold text-neutral-9 uppercase tracking-wider"
                  >Site web officiel</span
                >
                <span
                  class="text-xs font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
                >
                  {{ socialLinks()!.website }}
                </span>
              </div>
              <mat-icon class="text-neutral-7 group-hover:text-neutral-11 !w-4 !h-4 !text-base shrink-0"
                >open_in_new</mat-icon
              >
            </a>
          }
          @if (socialLinks()?.googleReviewUrl) {
            <a
              [href]="socialLinks()!.googleReviewUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 p-4 bg-white hover:bg-neutral-1 border border-neutral-3 hover:border-neutral-4 rounded-2xl transition-all group shadow-xs cursor-pointer"
            >
              <div
                class="w-9 h-9 rounded-lg bg-amber-2 text-amber-11 flex items-center justify-center shrink-0"
              >
                <mat-icon class="!w-4.5 !h-4.5 !text-lg">star</mat-icon>
              </div>
              <div class="flex flex-col min-w-0 flex-1">
                <span class="text-[11px] font-semibold text-neutral-9 uppercase tracking-wider"
                  >Avis Google</span
                >
                <span
                  class="text-xs font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
                >
                  Consulter les avis clients
                </span>
              </div>
              <mat-icon class="text-neutral-7 group-hover:text-neutral-11 !w-4 !h-4 !text-base shrink-0"
                >open_in_new</mat-icon
              >
            </a>
          }
          @if (socialLinks()?.opinionSystemUrl) {
            <a
              [href]="socialLinks()!.opinionSystemUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 p-4 bg-white hover:bg-neutral-1 border border-neutral-3 hover:border-neutral-4 rounded-2xl transition-all group shadow-xs cursor-pointer"
            >
              <div
                class="w-9 h-9 rounded-lg bg-blue-2 text-blue-11 flex items-center justify-center shrink-0"
              >
                <mat-icon class="!w-4.5 !h-4.5 !text-lg">verified</mat-icon>
              </div>
              <div class="flex flex-col min-w-0 flex-1">
                <span class="text-[11px] font-semibold text-neutral-9 uppercase tracking-wider"
                  >Immodvisor</span
                >
                <span
                  class="text-xs font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
                >
                  Attestation d'avis certifiés
                </span>
              </div>
              <mat-icon class="text-neutral-7 group-hover:text-neutral-11 !w-4 !h-4 !text-base shrink-0"
                >open_in_new</mat-icon
              >
            </a>
          }
          @if (socialLinks()?.linkedin) {
            <a
              [href]="socialLinks()!.linkedin"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 p-4 bg-white hover:bg-neutral-1 border border-neutral-3 hover:border-neutral-4 rounded-2xl transition-all group shadow-xs cursor-pointer"
            >
              <div class="w-9 h-9 rounded-lg bg-sky-2 text-sky-11 flex items-center justify-center shrink-0">
                <mat-icon class="!w-4.5 !h-4.5 !text-lg">work</mat-icon>
              </div>
              <div class="flex flex-col min-w-0 flex-1">
                <span class="text-[11px] font-semibold text-neutral-9 uppercase tracking-wider"
                  >Profil LinkedIn</span
                >
                <span
                  class="text-xs font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
                >
                  Voir le réseau professionnel
                </span>
              </div>
              <mat-icon class="text-neutral-7 group-hover:text-neutral-11 !w-4 !h-4 !text-base shrink-0"
                >open_in_new</mat-icon
              >
            </a>
          }
        </div>
      } @else {
        <div
          class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-2xl bg-white shadow-xs max-w-full"
        >
          <p class="text-xs text-neutral-9 italic whitespace-normal break-words max-w-md mx-auto">
            Aucun réseau social ou lien d'avis en ligne renseigné pour le moment.
          </p>
        </div>
      }
    }

    @case ('officeHours') {
      <!-- Adresse & Horaires (items fond blanc style card) -->
      <div class="flex flex-col gap-5">
        <!-- Coordonnées & Adresse de réception (fond blanc card) -->
        @if (resolvedAddress(); as addr) {
          <div
            class="p-4 bg-white rounded-2xl border border-neutral-3 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          >
            <div class="flex items-start gap-3 min-w-0">
              <div
                class="w-10 h-10 rounded-xl bg-primary-2 text-primary-11 flex items-center justify-center shrink-0 mt-0.5"
              >
                <mat-icon class="!w-5 !h-5 !text-xl">place</mat-icon>
              </div>
              <div class="flex flex-col min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-bold text-neutral-12">{{ addr.line1 }}</span>
                  <span
                    class="text-[10px] font-semibold px-2 py-0.5 rounded-full border shadow-2xs"
                    [class.bg-primary-2]="!addr.isCustom"
                    [class.text-primary-11]="!addr.isCustom"
                    [class.border-primary-4]="!addr.isCustom"
                    [class.bg-neutral-2]="addr.isCustom"
                    [class.text-neutral-10]="addr.isCustom"
                    [class.border-neutral-3]="addr.isCustom"
                  >
                    {{ addr.sourceLabel }}
                  </span>
                </div>
                @if (addr.complement) {
                  <span class="text-xs text-neutral-10">{{ addr.complement }}</span>
                }
                <span class="text-xs text-neutral-10 font-medium">{{ addr.line2 }}</span>
              </div>
            </div>

            @if (mapsUrl()) {
              <a
                [href]="mapsUrl()!"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-primary-11 bg-primary-2 hover:bg-primary-3 border border-primary-4 rounded-xl transition-all shrink-0 cursor-pointer self-start sm:self-auto shadow-2xs"
              >
                <mat-icon class="!w-3.5 !h-3.5 !text-sm">directions</mat-icon>
                <span>Itinéraire</span>
              </a>
            }
          </div>
        } @else {
          <div
            class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-2xl bg-white shadow-xs max-w-full"
          >
            <p class="text-xs text-neutral-9 italic">Aucune adresse professionnelle renseignée.</p>
          </div>
        }

        <!-- Horaires d'ouverture -->
        <div class="space-y-3">
          <div class="flex items-center justify-between flex-wrap gap-2">
            <span
              class="text-xs font-bold text-neutral-12 uppercase tracking-wider flex items-center gap-1.5"
            >
              <mat-icon class="!w-4 !h-4 !text-base text-primary-9">schedule</mat-icon>
              Horaires d'ouverture & permanence
            </span>

            <span
              class="text-xs font-bold px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 shadow-2xs"
              [class]="openingHoursStatus().badgeClass"
            >
              <span
                class="w-2 h-2 rounded-full"
                [class.bg-primary-9]="openingHoursStatus().isOpen"
                [class.bg-neutral-6]="!openingHoursStatus().isOpen"
              ></span>
              {{ openingHoursStatus().text }}
            </span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            @for (day of weekSchedule(); track day.key) {
              <div
                class="p-3 rounded-xl border text-xs flex flex-col gap-1 transition-all shadow-2xs"
                [class.bg-primary-1]="day.isToday && day.isOpen"
                [class.border-primary-4]="day.isToday && day.isOpen"
                [class.bg-white]="!day.isToday || !day.isOpen"
                [class.border-neutral-3]="!day.isToday || !day.isOpen"
              >
                <div class="flex items-center justify-between">
                  <span class="font-bold text-neutral-12 flex items-center gap-1">
                    {{ day.label }}
                    @if (day.isToday) {
                      <span class="text-[9px] font-bold px-1.5 py-0.2 rounded bg-primary-3 text-primary-11"
                        >Aujourd'hui</span
                      >
                    }
                  </span>
                  @if (!day.isOpen) {
                    <span class="text-[10px] text-neutral-8 font-medium">Fermé</span>
                  }
                </div>
                <span
                  class="text-[11px] leading-tight"
                  [class.text-neutral-11]="day.isOpen"
                  [class.text-neutral-8]="!day.isOpen"
                  [class.italic]="!day.isOpen"
                >
                  {{ day.statusText }}
                </span>
              </div>
            }
          </div>

          @if (openingHours()?.notes) {
            <p
              class="text-xs text-neutral-10 italic bg-white p-3 rounded-xl border border-neutral-3 shadow-2xs"
            >
              💡 {{ openingHours()!.notes }}
            </p>
          }
        </div>
      </div>
    }

    @case ('expiredCards') {
      <!-- Expired Cards List -->
      <div class="flex flex-col gap-4">
        <div class="flex items-center text-xl leading-none font-semibold tracking-tight">
          Historique des cartes T expirées
        </div>

        @if (expiredCards() && expiredCards().length > 0) {
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            @for (card of expiredCards(); track card._id) {
              <div
                class="bg-white dark:bg-neutral-2 rounded-2xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-xs flex flex-col items-center gap-6"
              >
                <!-- 3D flip card with watermark -->
                <div class="w-full max-w-sm shrink-0">
                  <etxe-agent-card-display [card]="card" [isExpired]="true"></etxe-agent-card-display>
                </div>

                <!-- Textual metadata for quick inspection -->
                <div
                  class="w-full grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 text-xs text-neutral-11 pt-3 border-t border-neutral-2 dark:border-neutral-4"
                >
                  <div class="flex flex-col">
                    <span class="text-[11px] text-neutral-9 font-medium">Société</span>
                    <span class="font-semibold text-neutral-12 truncate">{{ card.companyName }}</span>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[11px] text-neutral-9 font-medium">Dirigeant</span>
                    <span class="font-semibold text-neutral-12 truncate">
                      {{ card.leader.firstName }} {{ card.leader.lastName | uppercase }}
                    </span>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[11px] text-neutral-9 font-medium">Date d'expiration</span>
                    <span class="font-bold text-red-11">
                      {{ card.expiresAt | date: 'dd/MM/yyyy' }} (Expirée)
                    </span>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[11px] text-neutral-9 font-medium">CCI émettrice</span>
                    <span class="font-semibold text-neutral-12 truncate">{{ card.cci.name }}</span>
                  </div>
                </div>
              </div>
            }
          </div>
        } @else {
          <div
            class="p-6 text-center border-2 border-dashed border-neutral-3 dark:border-neutral-4 rounded-2xl bg-white dark:bg-neutral-2 shadow-xs"
          >
            <p class="text-sm text-neutral-9 italic">Aucune ancienne carte expirée dans l'historique.</p>
          </div>
        }
      </div>
      <mat-divider class="my-4"></mat-divider>
    }

    @default {
      <!-- Full Display Mode ('all') -->
      <div class="flex flex-col gap-6 w-full max-w-4xl">
        <!-- Jauge de complétude du profil -->
        @if (isCompletionVisible()) {
          <div
            class="surface-default rounded-2xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm space-y-4 relative"
          >
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div class="flex items-center gap-3 pr-8 sm:pr-0">
                <div
                  class="w-12 h-12 rounded-xl flex items-center justify-center font-black text-sm shrink-0"
                  [class.bg-success-2]="profileCompletion() === 100"
                  [class.text-success-11]="profileCompletion() === 100"
                  [class.bg-primary-2]="profileCompletion() < 100"
                  [class.text-primary-11]="profileCompletion() < 100"
                >
                  @if (profileCompletion() === 100) {
                    <mat-icon class="text-success-11">check</mat-icon>
                  } @else {
                    {{ profileCompletion() }}%
                  }
                </div>
                <div>
                  <h3 class="text-base font-bold text-neutral-12">
                    @if (profileCompletion() === 100) {
                      Profil 100% complété !
                    } @else {
                      Complétude de votre profil
                    }
                  </h3>
                  <p class="text-xs text-neutral-11">
                    @if (profileCompletion() === 100) {
                      Félicitations ! Votre profil est complet et optimisé pour recevoir des mandats.
                    } @else {
                      Un profil détaillé inspire davantage confiance aux propriétaires vendeurs.
                    }
                  </p>
                </div>
              </div>

              @if (profileCompletion() < 100) {
                <span
                  class="text-xs font-semibold px-3 py-1 bg-neutral-2 text-neutral-11 border border-neutral-3 rounded-full shrink-0"
                >
                  {{ completionTip() }}
                </span>
              } @else {
                <!-- Croix pour fermer / masquer l'information de complétude -->
                <button
                  type="button"
                  mat-icon-button
                  class="!absolute top-3 right-3 text-neutral-9 hover:text-neutral-12"
                  (click)="onDismissCompletion()"
                  aria-label="Fermer"
                >
                  <mat-icon>close</mat-icon>
                </button>
              }
            </div>

            <!-- Barre de progression -->
            <div class="w-full bg-neutral-2 h-2.5 rounded-full overflow-hidden border border-neutral-3">
              <div
                class="h-full rounded-full transition-all duration-500 ease-out"
                [class.bg-success-9]="profileCompletion() === 100"
                [class.bg-primary-9]="profileCompletion() < 100"
                [style.width.%]="profileCompletion()"
              ></div>
            </div>
          </div>
        }

        <!-- Score de réactivité de l'agent -->
        <div
          class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-5 shadow-sm space-y-4"
        >
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div class="flex items-center gap-3.5">
              <div
                class="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                [class.bg-emerald-100]="isHighlyResponsive()"
                [class.text-emerald-700]="isHighlyResponsive()"
                [class.dark:bg-emerald-950/60]="isHighlyResponsive()"
                [class.dark:text-emerald-300]="isHighlyResponsive()"
                [class.bg-primary-2]="!isHighlyResponsive()"
                [class.text-primary-11]="!isHighlyResponsive()"
              >
                <mat-icon class="size-6">{{ isHighlyResponsive() ? 'bolt' : 'speed' }}</mat-icon>
              </div>
              <div>
                <div class="flex items-center gap-2 flex-wrap">
                  <h3 class="text-base font-bold text-neutral-12 m-0">Score de réactivité</h3>
                  @if (isHighlyResponsive()) {
                    <span
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 shadow-2xs"
                    >
                      <mat-icon class="size-3.5! text-xs! text-emerald-600 dark:text-emerald-400"
                        >bolt</mat-icon
                      >
                      <span>Agent très réactif</span>
                    </span>
                  }
                </div>
                <p class="text-xs text-neutral-11 m-0 mt-0.5">
                  Assiduité lors des demandes de visites et des propositions de mandat.
                </p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div
              class="bg-neutral-1 dark:bg-neutral-3/30 p-3.5 rounded-xl border border-neutral-3 flex flex-col justify-between"
            >
              <span class="text-xs font-medium text-neutral-10">Temps de réponse moyen</span>
              <span class="text-xl font-extrabold text-neutral-12 mt-1">
                {{ formattedResponseTime() || '< 1 h' }}
              </span>
              <span class="text-[11px] text-neutral-10 mt-0.5">Objectif : sous 4 h</span>
            </div>

            <div
              class="bg-neutral-1 dark:bg-neutral-3/30 p-3.5 rounded-xl border border-neutral-3 flex flex-col justify-between"
            >
              <span class="text-xs font-medium text-neutral-10">Taux de réponse</span>
              <span class="text-xl font-extrabold text-neutral-12 mt-1">
                {{ responseRatePercent() !== null ? responseRatePercent() + ' %' : '100 %' }}
              </span>
              <span class="text-[11px] text-neutral-10 mt-0.5">Délai limite : 24 h</span>
            </div>

            <div
              class="bg-neutral-1 dark:bg-neutral-3/30 p-3.5 rounded-xl border border-neutral-3 flex flex-col justify-between"
            >
              <span class="text-xs font-medium text-neutral-10">Expirations de délai</span>
              <span
                class="text-xl font-extrabold mt-1"
                [class.text-emerald-600]="expiredActionsCount() === 0"
                [class.text-red-9]="expiredActionsCount() > 0"
              >
                {{ expiredActionsCount() }} {{ expiredActionsCount() > 1 ? 'expirations' : 'expiration' }}
              </span>
              <span class="text-[11px] text-neutral-10 mt-0.5">Pénalise votre score</span>
            </div>
          </div>
        </div>

        <!-- Type de profil & Réseau -->
        <div
          class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-5 shadow-sm flex items-start gap-4"
        >
          <div class="p-3 rounded-lg bg-primary-2 text-primary-11 shrink-0">
            <mat-icon class="size-6">badge</mat-icon>
          </div>
          <div class="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div class="flex flex-col">
              <span class="text-xs text-neutral-10 font-medium uppercase tracking-wider"
                >Type d'exercice & Réseau</span
              >
              <div class="flex items-center gap-3 mt-1 flex-wrap">
                <span class="text-lg font-bold text-neutral-12">
                  @if (profile.type === 'titulaire') {
                    Titulaire Carte T
                  } @else {
                    Mandataire Immobilier
                  }
                </span>
                @if (network()) {
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold bg-primary-2 text-primary-11 border border-primary-4"
                  >
                    {{ network() }}
                  </span>
                }
              </div>
              <span class="text-xs text-neutral-10 mt-1">
                @if (profile.type === 'titulaire') {
                  Détenteur légal de la carte professionnelle d'agent immobilier.
                } @else {
                  Agent commercial ou mandataire rattaché à une agence ou réseau titulaire.
                }
              </span>
            </div>

            @if (showLinkToAgentPage()) {
              <div class="shrink-0">
                <a
                  [routerLink]="['/agents', profile._id]"
                  class="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold text-primary-11 bg-primary-3 hover:bg-primary-4 border border-primary-5 rounded-lg transition-all cursor-pointer font-sans"
                >
                  <mat-icon class="size-4 !text-sm">badge</mat-icon>
                  <span>Fiche agent détaillée</span>
                </a>
              </div>
            }
          </div>
        </div>

        <!-- Spécialités & Biens de prédilection -->
        @if (specialties() && specialties().length > 0) {
          <div
            class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
          >
            <h3
              class="text-base font-semibold text-neutral-12 flex items-center gap-2 border-b border-neutral-3 pb-3"
            >
              <mat-icon class="text-primary-9">home_work</mat-icon>
              Spécialités de transactions
            </h3>

            <div class="flex flex-wrap gap-2.5">
              @for (spec of specialties(); track spec) {
                @let info = getSpecialtyInfo(spec);
                <span
                  class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-neutral-2 text-neutral-12 border border-neutral-3 shadow-xs"
                >
                  <mat-icon class="!w-4 !h-4 !text-base text-primary-11">{{ info.icon }}</mat-icon>
                  <span>{{ info.label }}</span>
                </span>
              }
            </div>
          </div>
        }

        <!-- Prestations & Services offerts -->
        @if (services() && services().length > 0) {
          <div
            class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
          >
            <h3
              class="text-base font-semibold text-neutral-12 flex items-center gap-2 border-b border-neutral-3 pb-3"
            >
              <mat-icon class="text-primary-9">handshake</mat-icon>
              Prestations de services & atouts
            </h3>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              @for (srv of services(); track srv) {
                @let srvInfo = getServiceInfo(srv);
                <div
                  class="flex items-center gap-2.5 p-3 rounded-xl bg-neutral-2 text-neutral-12 border border-neutral-3 shadow-2xs"
                >
                  <div
                    class="w-8 h-8 rounded-lg bg-primary-2 text-primary-11 flex items-center justify-center shrink-0"
                  >
                    <mat-icon class="!w-4 !h-4 !text-base">{{ srvInfo.icon }}</mat-icon>
                  </div>
                  <span class="text-xs font-semibold">{{ srvInfo.label }}</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Tarifs & Honoraires -->
        @if (feeScaleUrl() || (averageCommissionRate() !== null && averageCommissionRate() !== undefined)) {
          <div
            class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
          >
            <h3
              class="text-base font-semibold text-neutral-12 flex items-center gap-2 border-b border-neutral-3 pb-3"
            >
              <mat-icon class="text-primary-9">payments</mat-icon>
              Honoraires & Barème tarifaire
            </h3>

            <div class="flex flex-wrap items-center gap-4">
              @if (averageCommissionRate() !== null && averageCommissionRate() !== undefined) {
                <div class="flex items-center gap-3 p-3.5 bg-neutral-2 rounded-xl border border-neutral-3">
                  <div
                    class="w-9 h-9 rounded-lg bg-primary-2 text-primary-11 flex items-center justify-center font-bold text-xs"
                  >
                    %
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[11px] font-medium text-neutral-10">Taux de commission moyen</span>
                    <span class="text-sm font-extrabold text-neutral-12">
                      {{ averageCommissionRate() | number: '1.1-2' }} %
                    </span>
                  </div>
                </div>
              }

              @if (feeScaleUrl()) {
                <a
                  [href]="feeScaleUrl()!"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold text-primary-11 bg-primary-2 hover:bg-primary-3 border border-primary-4 transition-all shadow-2xs cursor-pointer"
                >
                  <mat-icon class="!w-4 !h-4 !text-base">description</mat-icon>
                  <span>Consulter le barème d'honoraires officiel</span>
                  <mat-icon class="!w-3.5 !h-3.5 !text-sm">open_in_new</mat-icon>
                </a>
              }
            </div>
          </div>
        }

        <!-- Active Card Presentation -->
        <div
          class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
        >
          <h3
            class="text-base font-semibold text-neutral-12 flex items-center gap-2 border-b border-neutral-3 pb-3"
          >
            <mat-icon class="text-primary-9">credit_card</mat-icon>
            Carte professionnelle active
          </h3>

          @if (activeCard(); as card) {
            <div class="flex flex-col lg:flex-row items-center gap-8 py-4">
              <!-- The 3D flip card -->
              <div class="w-full max-w-sm shrink-0">
                <etxe-agent-card-display [card]="card"></etxe-agent-card-display>
              </div>

              <!-- Quick textual list of card values for copy/quick inspection -->
              <div
                class="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 text-sm text-neutral-11"
              >
                <div class="flex flex-col pb-2 border-b border-neutral-2">
                  <span class="text-xs text-neutral-9 font-medium">Numéro de carte</span>
                  <span class="font-semibold text-neutral-12 mt-0.5">{{ card.number }}</span>
                </div>
                <div class="flex flex-col pb-2 border-b border-neutral-2">
                  <span class="text-xs text-neutral-9 font-medium">Nom de l'entreprise</span>
                  <span class="font-semibold text-neutral-12 mt-0.5">{{ card.companyName }}</span>
                </div>
                <div class="flex flex-col pb-2 border-b border-neutral-2">
                  <span class="text-xs text-neutral-9 font-medium">Représentant Légal / Dirigeant</span>
                  <span class="font-semibold text-neutral-12 mt-0.5">
                    {{ card.leader.firstName }}
                    {{ card.leader.lastName | uppercase }}
                  </span>
                </div>
                <div class="flex flex-col pb-2 border-b border-neutral-2">
                  <span class="text-xs text-neutral-9 font-medium">SIREN</span>
                  <span class="font-semibold text-neutral-12 mt-0.5 font-mono">{{ card.siren }}</span>
                </div>
                <div class="flex flex-col pb-2 border-b border-neutral-2">
                  <span class="text-xs text-neutral-9 font-medium">Date d'expiration</span>
                  <span
                    class="font-semibold text-neutral-12 mt-0.5"
                    [class.text-error-11]="card.isActive === false"
                  >
                    {{ card.expiresAt | date: 'dd/MM/yyyy' }}
                    @if (card.isActive === false) {
                      (Expirée)
                    }
                  </span>
                </div>
                <div class="flex flex-col pb-2 border-b border-neutral-2">
                  <span class="text-xs text-neutral-9 font-medium">CCI émettrice</span>
                  <span class="font-semibold text-neutral-12 mt-0.5">{{ card.cci.name }}</span>
                </div>
              </div>
            </div>
          } @else {
            <div
              class="p-6 rounded-xl border-2 border-dashed border-amber-6 bg-amber-1 flex flex-col items-center text-center gap-3"
            >
              <div class="w-12 h-12 rounded-xl bg-amber-3 text-amber-11 flex items-center justify-center">
                <mat-icon class="!w-6 !h-6 !text-2xl">credit_card_off</mat-icon>
              </div>
              <div class="flex flex-col gap-1 max-w-lg">
                <span class="text-sm font-bold text-neutral-12">Aucune carte T active trouvée</span>
                <p class="text-xs text-neutral-11">
                  @if (expiredCards() && expiredCards().length > 0) {
                    Votre précédente carte professionnelle T a expiré et a été archivée dans l'historique. Une
                    carte valide est requise pour exercer.
                  } @else {
                    Aucune carte professionnelle active n'a été trouvée. Elle a probablement expiré ou doit
                    être renouvelée.
                  }
                </p>
              </div>
            </div>
          }
        </div>

        <!-- Villes prioritaires pour les alertes email -->
        @if (preferredCities() && preferredCities().length > 0) {
          <div
            class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
          >
            <div class="flex items-center justify-between border-b border-neutral-3 pb-3">
              <h3 class="text-base font-semibold text-neutral-12 flex items-center gap-2">
                <mat-icon class="text-primary-9">notifications_active</mat-icon>
                Communes Prioritaires pour les Alertes
              </h3>
              <span class="text-xs text-neutral-10 font-medium">
                {{ preferredCities().length }} / 5 communes suivies
              </span>
            </div>

            <div class="flex flex-wrap gap-2.5">
              @for (city of preferredCities(); track city.name + '-' + city.zipCode) {
                <span
                  class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-neutral-2 text-neutral-12 border border-neutral-3 shadow-xs"
                >
                  <mat-icon class="!w-4 !h-4 !text-base text-primary-11">mark_email_unread</mat-icon>
                  <span>{{ city.name }}</span>
                  <span class="font-mono text-neutral-10 text-[11px]">({{ city.zipCode }})</span>
                </span>
              }
            </div>
          </div>
        }

        <!-- Intervention Zones -->
        <div
          class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
        >
          <div class="flex items-center justify-between border-b border-neutral-3 pb-3">
            <h3 class="text-base font-semibold text-neutral-12 flex items-center gap-2">
              <mat-icon class="text-primary-9">map</mat-icon>
              Secteur d'Intervention
            </h3>
            @if (interventionZones() && interventionZones().length > 0) {
              <span class="text-xs text-neutral-10">
                {{ interventionZones()[0].departments?.length || 0 }} département(s) couvert(s)
              </span>
            }
          </div>

          <!-- Région administrative principale -->
          @if (primaryRegion()) {
            <div
              class="flex items-center gap-3.5 p-4 rounded-2xl bg-primary-2/40 border border-primary-4 shadow-xs"
            >
              <div
                class="w-10 h-10 rounded-xl bg-primary-3 text-primary-11 flex items-center justify-center shrink-0 shadow-xs"
              >
                <mat-icon class="!w-5 !h-5 !text-xl">explore</mat-icon>
              </div>
              <div class="flex flex-col min-w-0">
                <span class="text-[11px] font-bold text-primary-11 uppercase tracking-wider">
                  Région administrative principale
                </span>
                <span class="text-sm font-extrabold text-neutral-12 truncate">
                  {{ getRegionName(primaryRegion()) }}
                </span>
              </div>
            </div>
          }

          @if (interventionZones() && interventionZones().length > 0) {
            <div class="flex flex-col gap-4">
              @for (zone of interventionZones(); track zone.country) {
                @let regionGroups = getDepartmentsByRegion(zone.departments);
                <div class="flex flex-col gap-3">
                  <div class="flex items-center justify-between">
                    <span
                      class="text-xs font-bold text-neutral-11 uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <mat-icon class="!w-4 !h-4 !text-base text-neutral-9">public</mat-icon>
                      <span>Territoire national : {{ zone.country === 'FR' ? 'France' : zone.country }}</span>
                    </span>
                    <span class="text-xs text-neutral-10 font-medium">
                      {{ zone.departments?.length || 0 }} département(s) couvert(s)
                    </span>
                  </div>

                  @if (regionGroups.length > 0) {
                    <div class="flex flex-col gap-3">
                      @for (group of regionGroups; track group.regionName) {
                        <div
                          class="flex flex-col gap-2.5 p-4 rounded-2xl border transition-all"
                          [class.bg-primary-1/30]="group.isPrimary"
                          [class.border-primary-4]="group.isPrimary"
                          [class.bg-neutral-2]="!group.isPrimary"
                          [class.border-neutral-3]="!group.isPrimary"
                        >
                          <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                              <mat-icon
                                class="!w-4 !h-4 !text-base"
                                [class.text-primary-11]="group.isPrimary"
                                [class.text-neutral-9]="!group.isPrimary"
                              >
                                place
                              </mat-icon>
                              <span class="text-xs font-bold text-neutral-12">
                                {{ group.regionName }}
                              </span>
                              @if (group.isPrimary) {
                                <span
                                  class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary-3 text-primary-11"
                                >
                                  Région principale
                                </span>
                              }
                            </div>
                            <span
                              class="text-[11px] font-mono font-medium text-neutral-10 bg-neutral-3 px-2 py-0.5 rounded-full"
                            >
                              {{ group.departments.length }} dép.
                            </span>
                          </div>

                          <div class="flex flex-wrap gap-2 pt-1">
                            @for (dept of group.departments; track dept.code) {
                              <span
                                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs bg-white text-neutral-12 border border-neutral-3 shadow-xs hover:border-neutral-4 transition-colors"
                              >
                                <span
                                  class="font-mono text-[11px] font-extrabold px-1.5 py-0.5 rounded-md"
                                  [class.bg-primary-2]="group.isPrimary"
                                  [class.text-primary-11]="group.isPrimary"
                                  [class.bg-neutral-3]="!group.isPrimary"
                                  [class.text-neutral-11]="!group.isPrimary"
                                >
                                  {{ dept.code }}
                                </span>
                                <span class="font-medium">{{ dept.name }}</span>
                              </span>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  } @else {
                    <div
                      class="p-4 bg-primary-2/30 rounded-xl border border-primary-4 flex items-center gap-3"
                    >
                      <mat-icon class="text-primary-11">public</mat-icon>
                      <div class="flex flex-col">
                        <span class="text-xs font-bold text-primary-12">Couverture nationale intégrale</span>
                        <span class="text-xs text-neutral-11"
                          >Actif sur l'ensemble des départements français.</span
                        >
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          } @else {
            <div class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-xl">
              <p class="text-sm text-neutral-9 italic">Aucune zone d'exercice n'a été spécifiée.</p>
            </div>
          }
        </div>

        <!-- Présentation & Biographie -->
        @if (bio() && bio().trim().length > 0) {
          <div
            class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
          >
            <h3
              class="text-base font-semibold text-neutral-12 flex items-center gap-2 border-b border-neutral-3 pb-3"
            >
              <mat-icon class="text-primary-9">badge</mat-icon>
              Présentation & Biographie
            </h3>
            <div
              class="p-4 bg-neutral-2/50 rounded-xl border border-neutral-3 text-sm text-neutral-11 leading-relaxed whitespace-pre-line"
            >
              {{ bio() }}
            </div>
          </div>
        }

        <!-- Réseaux sociaux & Liens web -->
        @if (hasSocialLinks()) {
          <div
            class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
          >
            <h3
              class="text-base font-semibold text-neutral-12 flex items-center gap-2 border-b border-neutral-3 pb-3"
            >
              <mat-icon class="text-primary-9">share</mat-icon>
              Réseaux professionnels & Liens web
            </h3>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              @if (socialLinks()?.website) {
                <a
                  [href]="socialLinks()!.website"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-3 p-3.5 bg-neutral-1 hover:bg-neutral-2 border border-neutral-3 hover:border-neutral-4 rounded-xl transition-all group shadow-xs cursor-pointer"
                >
                  <div
                    class="w-9 h-9 rounded-lg bg-primary-2 text-primary-11 flex items-center justify-center shrink-0"
                  >
                    <mat-icon class="!w-4.5 !h-4.5 !text-lg">language</mat-icon>
                  </div>
                  <div class="flex flex-col min-w-0 flex-1">
                    <span class="text-[11px] font-semibold text-neutral-9 uppercase tracking-wider"
                      >Site web officiel</span
                    >
                    <span
                      class="text-xs font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
                    >
                      {{ socialLinks()!.website }}
                    </span>
                  </div>
                  <mat-icon class="text-neutral-7 group-hover:text-neutral-11 !w-4 !h-4 !text-base shrink-0"
                    >open_in_new</mat-icon
                  >
                </a>
              }
              @if (socialLinks()?.googleReviewUrl) {
                <a
                  [href]="socialLinks()!.googleReviewUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-3 p-3.5 bg-neutral-1 hover:bg-neutral-2 border border-neutral-3 hover:border-neutral-4 rounded-xl transition-all group shadow-xs cursor-pointer"
                >
                  <div
                    class="w-9 h-9 rounded-lg bg-amber-2 text-amber-11 flex items-center justify-center shrink-0"
                  >
                    <mat-icon class="!w-4.5 !h-4.5 !text-lg">star</mat-icon>
                  </div>
                  <div class="flex flex-col min-w-0 flex-1">
                    <span class="text-[11px] font-semibold text-neutral-9 uppercase tracking-wider"
                      >Avis Google</span
                    >
                    <span
                      class="text-xs font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
                    >
                      Consulter les avis clients
                    </span>
                  </div>
                  <mat-icon class="text-neutral-7 group-hover:text-neutral-11 !w-4 !h-4 !text-base shrink-0"
                    >open_in_new</mat-icon
                  >
                </a>
              }
              @if (socialLinks()?.opinionSystemUrl) {
                <a
                  [href]="socialLinks()!.opinionSystemUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-3 p-3.5 bg-neutral-1 hover:bg-neutral-2 border border-neutral-3 hover:border-neutral-4 rounded-xl transition-all group shadow-xs cursor-pointer"
                >
                  <div
                    class="w-9 h-9 rounded-lg bg-blue-2 text-blue-11 flex items-center justify-center shrink-0"
                  >
                    <mat-icon class="!w-4.5 !h-4.5 !text-lg">verified</mat-icon>
                  </div>
                  <div class="flex flex-col min-w-0 flex-1">
                    <span class="text-[11px] font-semibold text-neutral-9 uppercase tracking-wider"
                      >Immodvisor</span
                    >
                    <span
                      class="text-xs font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
                    >
                      Attestation d'avis certifiés
                    </span>
                  </div>
                  <mat-icon class="text-neutral-7 group-hover:text-neutral-11 !w-4 !h-4 !text-base shrink-0"
                    >open_in_new</mat-icon
                  >
                </a>
              }
              @if (socialLinks()?.linkedin) {
                <a
                  [href]="socialLinks()!.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-3 p-3.5 bg-neutral-1 hover:bg-neutral-2 border border-neutral-3 hover:border-neutral-4 rounded-xl transition-all group shadow-xs cursor-pointer"
                >
                  <div
                    class="w-9 h-9 rounded-lg bg-sky-2 text-sky-11 flex items-center justify-center shrink-0"
                  >
                    <mat-icon class="!w-4.5 !h-4.5 !text-lg">work</mat-icon>
                  </div>
                  <div class="flex flex-col min-w-0 flex-1">
                    <span class="text-[11px] font-semibold text-neutral-9 uppercase tracking-wider"
                      >Profil LinkedIn</span
                    >
                    <span
                      class="text-xs font-bold text-neutral-12 truncate group-hover:text-primary-11 transition-colors"
                    >
                      Voir le réseau professionnel
                    </span>
                  </div>
                  <mat-icon class="text-neutral-7 group-hover:text-neutral-11 !w-4 !h-4 !text-base shrink-0"
                    >open_in_new</mat-icon
                  >
                </a>
              }
            </div>
          </div>
        }

        <!-- Adresse professionnelle & Horaires -->
        @if (hasOfficeHours()) {
          <div
            class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-5"
          >
            <h3
              class="text-base font-semibold text-neutral-12 flex items-center gap-2 border-b border-neutral-3 pb-3"
            >
              <mat-icon class="text-primary-9">storefront</mat-icon>
              Adresse professionnelle & Horaires
            </h3>

            <!-- Coordonnées & Adresse de réception -->
            @if (resolvedAddress(); as addr) {
              <div
                class="p-4 bg-neutral-2/60 rounded-xl border border-neutral-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div class="flex items-start gap-3 min-w-0">
                  <div
                    class="w-9 h-9 rounded-lg bg-primary-2 text-primary-11 flex items-center justify-center shrink-0 mt-0.5"
                  >
                    <mat-icon class="!w-4.5 !h-4.5 !text-lg">place</mat-icon>
                  </div>
                  <div class="flex flex-col min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="text-xs font-bold text-neutral-12">{{ addr.line1 }}</span>
                      <span
                        class="text-[10px] font-semibold px-2 py-0.5 rounded-full border shadow-2xs"
                        [class.bg-primary-2]="!addr.isCustom"
                        [class.text-primary-11]="!addr.isCustom"
                        [class.border-primary-4]="!addr.isCustom"
                        [class.bg-neutral-2]="addr.isCustom"
                        [class.text-neutral-10]="addr.isCustom"
                        [class.border-neutral-3]="addr.isCustom"
                      >
                        {{ addr.sourceLabel }}
                      </span>
                    </div>
                    @if (addr.complement) {
                      <span class="text-xs text-neutral-10">{{ addr.complement }}</span>
                    }
                    <span class="text-xs text-neutral-10 font-medium">{{ addr.line2 }}</span>
                  </div>
                </div>

                @if (mapsUrl()) {
                  <a
                    [href]="mapsUrl()!"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-11 bg-primary-2 hover:bg-primary-3 border border-primary-4 rounded-lg transition-all shrink-0 cursor-pointer self-start sm:self-auto"
                  >
                    <mat-icon class="!w-3.5 !h-3.5 !text-sm">directions</mat-icon>
                    <span>Itinéraire</span>
                  </a>
                }
              </div>
            }

            <!-- Horaires d'ouverture -->
            <div class="space-y-3">
              <div class="flex items-center justify-between flex-wrap gap-2">
                <span
                  class="text-xs font-bold text-neutral-12 uppercase tracking-wider flex items-center gap-1.5"
                >
                  <mat-icon class="!w-4 !h-4 !text-base text-primary-9">schedule</mat-icon>
                  Horaires d'ouverture & permanence
                </span>

                <span
                  class="text-xs font-bold px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 shadow-2xs"
                  [class]="openingHoursStatus().badgeClass"
                >
                  <span
                    class="w-2 h-2 rounded-full"
                    [class.bg-primary-9]="openingHoursStatus().isOpen"
                    [class.bg-neutral-6]="!openingHoursStatus().isOpen"
                  ></span>
                  {{ openingHoursStatus().text }}
                </span>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                @for (day of weekSchedule(); track day.key) {
                  <div
                    class="p-2.5 rounded-xl border text-xs flex flex-col gap-1 transition-all"
                    [class.bg-primary-1]="day.isToday && day.isOpen"
                    [class.border-primary-4]="day.isToday && day.isOpen"
                    [class.shadow-xs]="day.isToday"
                    [class.bg-neutral-1]="!day.isToday"
                    [class.border-neutral-3]="!day.isToday"
                  >
                    <div class="flex items-center justify-between">
                      <span class="font-bold text-neutral-12 flex items-center gap-1">
                        {{ day.label }}
                        @if (day.isToday) {
                          <span
                            class="text-[9px] font-bold px-1.5 py-0.2 rounded bg-primary-3 text-primary-11"
                            >Aujourd'hui</span
                          >
                        }
                      </span>
                      @if (!day.isOpen) {
                        <span class="text-[10px] text-neutral-8 font-medium">Fermé</span>
                      }
                    </div>
                    <span
                      class="text-[11px] leading-tight"
                      [class.text-neutral-11]="day.isOpen"
                      [class.text-neutral-8]="!day.isOpen"
                      [class.italic]="!day.isOpen"
                    >
                      {{ day.statusText }}
                    </span>
                  </div>
                }
              </div>

              @if (openingHours()?.notes) {
                <p
                  class="text-xs text-neutral-10 italic bg-neutral-2/50 p-2.5 rounded-lg border border-neutral-3"
                >
                  💡 {{ openingHours()!.notes }}
                </p>
              }
            </div>
          </div>
        }

        <!-- Expired Cards List -->
        <div
          class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-sm flex flex-col gap-4"
        >
          <h3
            class="text-base font-semibold text-neutral-12 flex items-center gap-2 border-b border-neutral-3 pb-3"
          >
            <mat-icon class="text-neutral-9">history</mat-icon>
            Historique des Cartes T Expirées
          </h3>

          @if (expiredCards() && expiredCards().length > 0) {
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              @for (card of expiredCards(); track card._id) {
                <div
                  class="bg-white dark:bg-neutral-2 rounded-2xl border border-neutral-3 dark:border-neutral-4 p-6 shadow-xs flex flex-col items-center gap-6"
                >
                  <!-- 3D flip card with watermark -->
                  <div class="w-full max-w-sm shrink-0">
                    <etxe-agent-card-display [card]="card" [isExpired]="true"></etxe-agent-card-display>
                  </div>

                  <!-- Textual metadata for quick inspection -->
                  <div
                    class="w-full grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 text-xs text-neutral-11 pt-3 border-t border-neutral-2"
                  >
                    <div class="flex flex-col">
                      <span class="text-[11px] text-neutral-9 font-medium">Société</span>
                      <span class="font-semibold text-neutral-12 truncate">{{ card.companyName }}</span>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-[11px] text-neutral-9 font-medium">Dirigeant</span>
                      <span class="font-semibold text-neutral-12 truncate">
                        {{ card.leader.firstName }} {{ card.leader.lastName | uppercase }}
                      </span>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-[11px] text-neutral-9 font-medium">Date d'expiration</span>
                      <span class="font-bold text-red-11">
                        {{ card.expiresAt | date: 'dd/MM/yyyy' }} (Expirée)
                      </span>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-[11px] text-neutral-9 font-medium">CCI émettrice</span>
                      <span class="font-semibold text-neutral-12 truncate">{{ card.cci.name }}</span>
                    </div>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="p-6 text-center border-2 border-dashed border-neutral-3 rounded-xl">
              <p class="text-sm text-neutral-9 italic">Aucune ancienne carte expirée dans l'historique.</p>
            </div>
          }
        </div>
      </div>
    }
  }
} @else {
  <div
    class="surface-default rounded-xl border border-neutral-3 dark:border-neutral-4 p-8 text-center shadow-sm"
  >
    <div
      class="w-16 h-16 bg-neutral-2 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-9"
    >
      <mat-icon class="!text-3xl !w-8 !h-8">no_accounts</mat-icon>
    </div>
    <h3 class="text-lg font-bold text-neutral-12 mb-2">Profil Agent inexistant</h3>
    <p class="text-sm text-neutral-9 max-w-md mx-auto leading-relaxed">
      Cet utilisateur n'a pas encore créé ou configuré de profil de professionnel de l'immobilier sur la
      plateforme.
    </p>
  </div>
}
